import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type User,
  type Auth,
} from 'firebase/auth'
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported as messagingIsSupported,
  type Messaging,
  type MessagePayload,
} from 'firebase/messaging'

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// Initialize Firebase
let app: FirebaseApp | null = null
let auth: Auth | null = null

const initializeFirebase = (): void => {
  if (!app && firebaseConfig.apiKey) {
    app = initializeApp(firebaseConfig)
    auth = getAuth(app)
  }
}

// Initialize on module load
initializeFirebase()

// Auth providers
const googleProvider = new GoogleAuthProvider()
googleProvider.setCustomParameters({
  prompt: 'select_account',
})

const microsoftProvider = new OAuthProvider('microsoft.com')
if (import.meta.env.VITE_AZURE_TENANT_ID) {
  microsoftProvider.setCustomParameters({
    tenant: import.meta.env.VITE_AZURE_TENANT_ID,
  })
}

// Export auth instance getter
export const getFirebaseAuth = (): Auth | null => auth

// ─────────── Push messaging ───────────
// Lazily initialized — getMessaging needs a service worker and many
// browsers don't support it (Safari < 16.4 desktop, iOS PWA quirks).
// VAPID public key comes from Firebase console → Cloud Messaging → Web Push.

let messaging: Messaging | null = null

export const getFirebaseMessaging = async (): Promise<Messaging | null> => {
  if (!app) return null
  if (messaging) return messaging
  try {
    if (!(await messagingIsSupported())) return null
    messaging = getMessaging(app)
    return messaging
  } catch (e) {
    console.warn('[firebase] messaging unsupported:', e)
    return null
  }
}

export const requestFcmToken = async (
  serviceWorkerRegistration: ServiceWorkerRegistration,
): Promise<string | null> => {
  const m = await getFirebaseMessaging()
  if (!m) return null
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY
  if (!vapidKey) {
    console.warn('[firebase] VITE_FIREBASE_VAPID_KEY missing — push disabled')
    return null
  }
  return getToken(m, { vapidKey, serviceWorkerRegistration })
}

export const onForegroundMessage = async (
  handler: (p: MessagePayload) => void,
): Promise<() => void> => {
  const m = await getFirebaseMessaging()
  if (!m) return () => {}
  return onMessage(m, handler)
}

// Export providers and methods
export {
  googleProvider,
  microsoftProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type User,
}
